"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { useAuthDialogStore } from "@/features/user/ui/store";
import { redirect } from "@/i18n/navigation";
import {
  AnalyticsEvents,
  identifyUser,
  trackEvent,
} from "@/lib/client/analytics/product-analytics";
import { authClient } from "@/lib/client/auth/client";
import {
  Box,
  Button,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HStack,
  Input,
  Text,
  VStack,
} from "@/ui";

// define form schema
export const formSchema = z.object({
  email: z.string().email().min(1).max(50),
  password: z.string().min(1),
});

/**
 * SignInForm component - Sign in form
 */
export const SignInForm = () => {
  const locale = useLocale();
  const toastT = useTranslations("toast");
  const generalT = useTranslations("general");
  const validationT = useTranslations("validation");
  const usersT = useTranslations("users");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [signInError, setSignInError] = React.useState<string>();
  const { toggleAuthDialog } = useAuthDialogStore();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = form;
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const result = await authClient.signIn.email({
        email: values.email,
        password: values.password,
      });

      if (result.error) {
        trackEvent(AnalyticsEvents.AUTH_SIGN_IN_FAILED, {
          error_code: result.error.code || "unknown",
          error_message: result.error.message,
        });
        setSignInError(result.error.message);
      } else {
        // Signed in
        identifyUser(values.email, { email: values.email });
        trackEvent(AnalyticsEvents.AUTH_SIGN_IN_COMPLETED, {
          method: "email",
          email: values.email,
        });
        toast.success(toastT("success.signed_in"), {
          description: toastT("success.title"),
          duration: 5000,
        });
        reset();
        setSignInError(undefined);
        toggleAuthDialog(false);
        redirect({ href: "/", locale: locale });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign in";
      trackEvent(AnalyticsEvents.AUTH_SIGN_IN_FAILED, {
        error_code: "exception",
        error_message: errorMessage,
      });
      setSignInError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onResetPassword = async (email: string) => {
    if (email) {
      try {
        setSignInError(undefined);

        trackEvent(AnalyticsEvents.AUTH_PASSWORD_RESET_REQUESTED, { email });

        const result = await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/${locale}/user/reset-password`,
        });

        if (result.error) {
          toast.error(result.error.message);
        } else {
          toggleAuthDialog(false);
          toast.success(toastT("success.password_reset_check_inbox"), {
            description: toastT("success.password_reset_submitted"),
            duration: 5000,
          });
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to send reset email");
      }
    } else {
      setSignInError(toastT("info.email_required"));
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full"
      >
        <VStack className="w-full items-center space-y-3">
          <Text className="min-h-4 text-center text-sm italic text-destructive">{signInError}</Text>
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full space-y-1.5">
                <FormControl>
                  <Input
                    placeholder={generalT("email")}
                    {...field}
                  />
                </FormControl>
                <FormMessage>
                  {errors.email && validationT("required", { field: "email" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full space-y-1.5">
                <FormControl>
                  <HStack className="items-center justify-end">
                    <Input
                      placeholder={generalT("password")}
                      {...field}
                      type={showPassword ? "text" : "password"}
                    />
                    <Box
                      onClick={handleTogglePassword}
                      className="absolute mr-2 cursor-pointer"
                    >
                      {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    </Box>
                  </HStack>
                </FormControl>
                <FormMessage>
                  {errors.password && validationT("required", { field: "password" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="default"
            disabled={isLoading || !form.formState.isValid}
            className="mt-1 h-11 w-full"
          >
            <span className={cn(isLoading ? "loading loading-spinner" : "hidden")} />
            {usersT("sign_in")}
          </Button>
          <Text
            className="link w-fit cursor-pointer text-sm"
            onClick={() => onResetPassword(getValues("email"))}
          >
            {usersT("forgot_pass")}
          </Text>
        </VStack>
      </form>
    </Form>
  );
};
